import React from 'react';
import { useSourceManager } from '@/hooks/useSourceManager';
import { Customer } from '@/hooks/useCustomers';

interface SourceType {
  id: string;
  name: string;
  icon: string;
  default: boolean;
  description: string;
}

interface CustomerSourcesListProps {
  customers: Customer[];
  selectedCustomer: Customer | null;
  onSelectCustomer: (customer: Customer) => void;
  activeSourceType: string;
  sourceTypes: SourceType[];
}

export const CustomerSourcesList: React.FC<CustomerSourcesListProps> = ({ 
  customers, 
  selectedCustomer, 
  onSelectCustomer, 
  activeSourceType,
  sourceTypes 
}) => {
  return (
    <div className="w-1/3 bg-muted border-r overflow-y-auto">
      <div className="p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-medium text-foreground">
            Customers ({customers.length})
          </h3>
          <span className="text-xs text-muted-foreground">
            Filter: {activeSourceType === 'all' ? 'All Sources' : sourceTypes.find(s => s.id === activeSourceType)?.name}
          </span>
        </div>
        
        <div className="space-y-2">
          {customers.map(customer => {
            const isSelected = selectedCustomer?.id === customer.id;
            
            return (
              <CustomerSourceCard
                key={customer.id}
                customer={customer}
                isSelected={isSelected}
                onClick={() => onSelectCustomer(customer)}
                activeSourceType={activeSourceType}
                sourceTypes={sourceTypes}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CustomerSourceCard: React.FC<{
  customer: Customer;
  isSelected: boolean;
  onClick: () => void;
  activeSourceType: string;
  sourceTypes: SourceType[];
}> = ({ customer, isSelected, onClick, activeSourceType, sourceTypes }) => {
  const { sources, loading } = useSourceManager(customer.id);

  const getSourceStatusIcon = (sourceId: string) => {
    const source = sources[sourceId];
    if (loading) return '⏳';
    
    switch (source?.status) {
      case 'active': return '✅';
      case 'error': return '❌';
      case 'pending': return '⏳';
      case 'setup_required': return '⚙️';
      default: return '⭕';
    }
  };

  const getSourceStatusColor = (sourceId: string) => {
    const source = sources[sourceId];
    switch (source?.status) {
      case 'active': return 'bg-green-500/10 border-green-500/20';
      case 'error': return 'bg-red-500/10 border-red-500/20';
      case 'pending': return 'bg-yellow-500/10 border-yellow-500/20';
      case 'setup_required': return 'bg-blue-500/10 border-blue-500/20';
      default: return 'bg-muted border-border';
    }
  };

  const getActiveSourceCount = () => {
    return Object.values(sources).filter(source => source?.status === 'active').length;
  };

  return (
    <div
      className={`p-3 rounded-lg cursor-pointer transition-all ${
        isSelected 
          ? 'bg-primary/10 border-2 border-primary/20 shadow-sm' 
          : 'bg-card hover:bg-muted border border-border'
      }`}
      onClick={onClick}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground truncate">
            {customer.name}
          </h4>
          <p className="text-xs text-muted-foreground truncate">
            {customer.email}
          </p>
        </div>
        <div className="text-right ml-2">
          <span className={`text-xs px-2 py-1 rounded font-medium ${
            getActiveSourceCount() > 0 
              ? 'bg-green-500/10 text-green-700' 
              : 'bg-muted text-muted-foreground'
          }`}>
            {getActiveSourceCount()}/{sourceTypes.length} active
          </span>
        </div>
      </div>

      {/* Real-time Source Status Grid */}
      <div className="grid grid-cols-4 gap-1 mt-2">
        {sourceTypes.slice(0, 4).map(source => (
          <div
            key={source.id}
            className={`text-center p-1 rounded text-xs border ${
              getSourceStatusColor(source.id)
            } ${
              activeSourceType === source.id || activeSourceType === 'all'
                ? 'ring-2 ring-primary/30'
                : ''
            }`}
            title={`${source.name}: ${sources[source.id]?.status || 'inactive'}`}
          >
            <div className="text-sm leading-none">{source.icon}</div>
            <div className="text-xs mt-1">{getSourceStatusIcon(source.id)}</div>
          </div>
        ))}
      </div>
      
      {/* Second row for remaining sources */}
      <div className="grid grid-cols-3 gap-1 mt-1">
        {sourceTypes.slice(4).map(source => (
          <div
            key={source.id}
            className={`text-center p-1 rounded text-xs border ${
              getSourceStatusColor(source.id)
            } ${
              activeSourceType === source.id || activeSourceType === 'all'
                ? 'ring-2 ring-primary/30'
                : ''
            }`}
            title={`${source.name}: ${sources[source.id]?.status || 'inactive'}`}
          >
            <div className="text-sm leading-none">{source.icon}</div>
            <div className="text-xs mt-1">{getSourceStatusIcon(source.id)}</div>
          </div>
        ))}
      </div>

      {/* Default mailbox address */}
      <div className="mt-2 p-2 bg-muted rounded">
        <div className="text-xs text-muted-foreground">Default mailbox:</div>
        <code className="text-xs font-mono text-primary">
          bills+{customer.name.toLowerCase().replace(/\s+/g, '')}@billportal.ai
        </code>
      </div>
    </div>
  );
};